import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, type AdminOverview, type TenantRollup } from '../../lib/api';
import { formatDate, formatDateTime, plural } from '../../lib/format';

/**
 * Every carrier on the platform, one row each.
 *
 * Ordered by what needs a person, not alphabetically: a console that opens on the same
 * carrier every morning regardless of what happened overnight is a console that gets read
 * once and then skimmed.
 */

type Filter = 'all' | 'attention' | 'unevaluated' | 'billing';

/** Days before a carrier's stored evaluation is old enough to distrust. */
const STALE_AFTER_DAYS = 2;

function daysSince(dateish: string | null): number | null {
  if (!dateish) return null;
  const then = Date.parse(`${dateish.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(then)) return null;
  const today = Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);
  return Math.round((today - then) / 86_400_000);
}

function needsAttention(t: TenantRollup): boolean {
  return (
    t.driversNonCompliant > 0 ||
    t.pendingReviewArtifacts > 0 ||
    t.alertsBounced7d > 0 ||
    t.status === 'past_due' ||
    t.status === 'suspended'
  );
}

function statusPill(status: string): { className: string; label: string } | null {
  if (status === 'active') return null;
  if (status === 'past_due') return { className: 'pill is-bad', label: 'past due' };
  if (status === 'suspended') return { className: 'pill is-bad', label: 'suspended' };
  if (status === 'canceled') return { className: 'pill', label: 'canceled' };
  if (status === 'trialing') return { className: 'pill is-warn', label: 'trial' };
  return { className: 'pill', label: status };
}

export function Customers() {
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api.get<AdminOverview>('/api/admin/overview'),
  });

  const visible = useMemo(() => {
    if (!data) return [];
    const needle = query.trim().toLowerCase();

    return data.tenants
      .filter((t) => {
        if (filter === 'attention' && !needsAttention(t)) return false;
        if (filter === 'unevaluated' && t.lastEvaluatedFor !== null) return false;
        if (filter === 'billing' && t.status !== 'past_due' && t.status !== 'suspended') {
          return false;
        }
        if (needle) {
          const hay = `${t.name} ${t.slug} ${t.dotNumber ?? ''}`.toLowerCase();
          if (!hay.includes(needle)) return false;
        }
        return true;
      })
      .sort(
        (a, b) =>
          b.driversNonCompliant - a.driversNonCompliant ||
          b.pendingReviewArtifacts - a.pendingReviewArtifacts ||
          a.name.localeCompare(b.name),
      );
  }, [data, filter, query]);

  if (isPending) return <p className="muted">Loading the platform…</p>;
  if (error) return <p className="field-error">{(error as Error).message}</p>;
  if (!data) return null;

  const s = data.summary;
  const staleDays = daysSince(s.stalestEvaluatedFor);
  const neverEvaluated = data.tenants.filter((t) => t.lastEvaluatedFor === null).length;

  return (
    <div>
      <header className="admin-head">
        <p className="eyebrow">Milepost Safety Group · platform console</p>
        <h1 className="admin-title">Customers</h1>

        <p className="admin-ledger">
          {s.tenantsTotal === 0 ? (
            <>No carriers provisioned yet.</>
          ) : (
            <>
              <span className={`num ${s.driversNonCompliant > 0 ? 'tone-bad' : 'tone-ok'}`}>
                {s.driversNonCompliant}
              </span>{' '}
              {plural(s.driversNonCompliant, 'driver')}{' '}
              {s.driversNonCompliant === 1 ? 'has a file that' : 'have files that'} would not
              survive an audit today, across{' '}
              {s.tenantsTotal === 1 ? 'the one carrier' : `${s.tenantsTotal} carriers`}.{' '}
              {s.pendingReviewTotal > 0 && (
                <>
                  {s.pendingReviewTotal} {plural(s.pendingReviewTotal, 'upload')}{' '}
                  {s.pendingReviewTotal === 1 ? 'is' : 'are'} waiting on carrier staff.{' '}
                </>
              )}
              {s.alertsBounced7d > 0 && (
                <>
                  {s.alertsBounced7d} alert {plural(s.alertsBounced7d, 'delivery')} failed this
                  week — a bounced compliance alert means a carrier believes they are being
                  watched and nobody is.
                </>
              )}
            </>
          )}
        </p>
      </header>

      {/*
        Freshness first, before any of the numbers it qualifies. Every compliance figure
        here comes from driver_evaluations — what the nightly scan last wrote — not from a
        live evaluation. When the scan slips, the figures are not wrong so much as old, and
        an operator needs to know which they are reading.
      */}
      {(staleDays === null || staleDays >= STALE_AFTER_DAYS || neverEvaluated > 0) && (
        <div className="stale-notice" role="status">
          <strong>Figures may be stale.</strong>
          <span>
            {staleDays === null
              ? 'No carrier has been evaluated yet — the nightly scan has not run.'
              : `The carrier with the oldest evaluation was last scanned ${
                  staleDays === 0 ? 'today' : `${staleDays} ${plural(staleDays, 'day')} ago`
                } (${formatDate(s.stalestEvaluatedFor)}).`}
            {neverEvaluated > 0 &&
              ` ${neverEvaluated} ${plural(neverEvaluated, 'carrier')} ${
                neverEvaluated === 1 ? 'has' : 'have'
              } never been evaluated; their compliance columns are blank, not zero.`}
          </span>
        </div>
      )}

      <div className="activity-filters">
        <div className="field">
          <label htmlFor="carrier-search">Search</label>
          <input
            id="carrier-search"
            type="text"
            value={query}
            placeholder="Name, slug, or USDOT"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="carrier-filter">Show</label>
          <select
            id="carrier-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value as Filter)}
          >
            <option value="all">All carriers ({data.tenants.length})</option>
            <option value="attention">
              Needs attention ({data.tenants.filter(needsAttention).length})
            </option>
            <option value="unevaluated">Never evaluated ({neverEvaluated})</option>
            <option value="billing">
              Billing trouble (
              {data.tenants.filter((t) => t.status === 'past_due' || t.status === 'suspended').length}
              )
            </option>
          </select>
        </div>
      </div>

      <div className="card scroll-x">
        <table>
          <thead>
            <tr>
              <th>Carrier</th>
              <th>Workspace</th>
              <th className="num-cell">Drivers</th>
              <th className="num-cell">Not audit-ready</th>
              <th className="num-cell">Expiring</th>
              <th className="num-cell">To review</th>
              <th className="num-cell">Staff</th>
              <th>Plan</th>
              <th>Owner</th>
              <th>Last evaluated</th>
              <th>Last activity</th>
              <th className="num-cell">Bounces&nbsp;7d</th>
              {/* The row menu. Header is blank by design; the label lives on each button. */}
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={13} className="muted">
                  No carriers match.
                </td>
              </tr>
            )}
            {visible.map((t) => {
              const pill = statusPill(t.status);
              const evaluated = t.lastEvaluatedFor !== null;
              return (
                <tr key={t.id}>
                  <td>
                    <div className="carrier-name">
                      <Link to={`/customers/${t.id}`}>
                        <strong>{t.name}</strong>
                      </Link>
                      {t.dotNumber && <span className="cite">USDOT {t.dotNumber}</span>}
                    </div>
                  </td>
                  <td>
                    {pill ? <span className={pill.className}>{pill.label}</span> : <span className="pill is-ok">active</span>}
                  </td>
                  <td className="num-cell tnum">{t.drivers.active + t.drivers.applicant}</td>
                  {/*
                    Blank, not zero, when nothing has been evaluated. "0 not audit-ready"
                    is a claim about the fleet; "—" is an admission that we have not looked.
                  */}
                  <td
                    className={`num-cell tnum ${
                      evaluated && t.driversNonCompliant > 0
                        ? 'is-bad'
                        : evaluated
                          ? 'is-zero'
                          : ''
                    }`}
                  >
                    {evaluated ? t.driversNonCompliant : <span className="never-evaluated">—</span>}
                  </td>
                  <td className="num-cell tnum">
                    {evaluated ? t.requirements.expiringSoon : '—'}
                  </td>
                  <td className={`num-cell tnum ${t.pendingReviewArtifacts === 0 ? 'is-zero' : ''}`}>
                    {t.pendingReviewArtifacts}
                  </td>
                  <td className="num-cell tnum">{t.staffActive}</td>
                  <td>
                    <PlanCell tenant={t} />
                  </td>
                  <td>
                    {t.accountOwner ?? <span className="muted">unassigned</span>}
                  </td>
                  <td className="tnum">
                    {evaluated ? (
                      formatDate(t.lastEvaluatedFor)
                    ) : (
                      <span className="never-evaluated">never</span>
                    )}
                  </td>
                  <td className="tnum">{formatDateTime(t.lastActivityAt)}</td>
                  <td className={`num-cell tnum ${t.alertsBounced7d > 0 ? 'is-bad' : 'is-zero'}`}>
                    {t.alertsBounced7d}
                  </td>
                  <td className="row-menu-cell">
                    <RowMenu tenant={t} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Subscription state, with the date that actually matters for it.
 *
 * Which date depends on the state: a trial is defined by when it ends, a delinquent account by
 * how long it has been failing, an active one by when it next renews. Showing all three would be
 * noise, and showing the wrong one is worse than showing none.
 */
function PlanCell({ tenant }: { tenant: TenantRollup }) {
  const status = tenant.subscriptionStatus;

  const detail =
    status === 'trialing' && tenant.trialEndsAt
      ? `ends ${formatDate(tenant.trialEndsAt)}`
      : (status === 'past_due' || status === 'unpaid') && tenant.delinquentSince
        ? `failing since ${formatDate(tenant.delinquentSince)}`
        : status === 'active' && tenant.currentPeriodEnd
          ? `renews ${formatDate(tenant.currentPeriodEnd)}`
          : null;

  return (
    <div className="plan-cell">
      <span className="plan-status">{status === 'none' ? 'no subscription' : status.replace('_', ' ')}</span>
      {detail && <span className="cite">{detail}</span>}
    </div>
  );
}

/**
 * The per-row action menu.
 *
 * Everything here is a READ. The console has exactly one state-changing endpoint — impersonation —
 * and that is a property worth keeping rather than eroding with a convenience menu, so there is no
 * suspend, no status change, and no billing write.
 *
 * Two deliberate absences:
 *
 *  - Nothing driver-level. "Review uploads" and "see the drivers who are not audit-ready" both
 *    require naming drivers, and the console is aggregate-only by construction (migration 011,
 *    and the rule in packages/shared/src/domain.ts). Those live inside an impersonated session,
 *    where the carrier can see in their own audit trail that we looked.
 *  - Billing links OUT to Stripe rather than editing anything. Stripe is the source of truth and
 *    a second writer here would be overwritten by its next webhook.
 *
 * "View as customer" navigates to the reason box on the customer page rather than opening its own
 * prompt — the reason is mandatory (a CHECK constraint enforces it in the database, not just the
 * route), and one implementation of that form is easier to keep honest than two.
 */
function RowMenu({ tenant }: { tenant: TenantRollup }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  return (
    <div className="row-menu" ref={wrapRef}>
      <button
        type="button"
        className="row-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for ${tenant.name}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">•••</span>
      </button>

      {open && (
        <div className="row-menu-list" role="menu">
          <Link role="menuitem" to={`/customers/${tenant.id}`} onClick={() => setOpen(false)}>
            View customer
          </Link>
          <Link
            role="menuitem"
            to={`/activity?tenantId=${tenant.id}`}
            onClick={() => setOpen(false)}
          >
            View activity
          </Link>

          <hr />

          <Link
            role="menuitem"
            to={`/customers/${tenant.id}#open-workspace`}
            onClick={() => setOpen(false)}
          >
            View as customer…
          </Link>

          {tenant.stripeCustomerId && (
            <>
              <hr />
              <a
                role="menuitem"
                href={`https://dashboard.stripe.com/customers/${tenant.stripeCustomerId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
              >
                Open in Stripe ↗
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}
